/*
 * @Author: Ryan
 * @Date: 2026-01-09
 * @Description: How to use section for Video Translator landing page
 */
import Image from 'next/image';
import React from 'react';

import StartButton from '@/3.0/ai-avatar-generator/seo/StartButton';
import { VIDEO_TRANSLATOR_URL } from '@/landing/video-translator/config';

import styles from './index.module.scss';

export interface StepData {
  number: number;
  title: string;
  description: string;
}

export interface HowToSectionProps {
  /** 区块标题 */
  title: string;
  /** 步骤列表 */
  steps: StepData[];
  /** CTA 按钮文本 */
  ctaText: string;
  /** 跳转链接 */
  ctaUrl?: string;
  /** 图片地址 */
  imageUrl?: string;
}

interface StepItemProps extends StepData {}

const StepItem: React.FC<StepItemProps> = ({ number, title, description }) => (
  <div className={styles.stepItem}>
    {/* 步骤编号 */}
    <div className={styles.stepNumber}>{number}</div>
    {/* 步骤内容 */}
    <div className={styles.stepContent}>
      <h3 className={styles.stepTitle}>{title}</h3>
      <p className={styles.stepDescription}>{description}</p>
    </div>
  </div>
);

const HowToSection: React.FC<HowToSectionProps> = ({
  title,
  steps,
  ctaText,
  ctaUrl,
  imageUrl = 'https://assets.falcocut.ai/tools/video-translator/how-to-translate-a-video-with-falcocut.webp',
}) => {
  const targetUrl = ctaUrl || VIDEO_TRANSLATOR_URL;

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        {/* 标题 */}
        <h2 className={styles.title}>{title}</h2>

        {/* 内容区域 */}
        <div className={styles.content}>
          {/* 左侧图片 */}
          <div className={styles.imageWrapper}>
            <div className={styles.imageContainer}>
              <Image
                src={imageUrl}
                alt={title}
                width={560}
                height={480}
                className={styles.image}
              />
            </div>
          </div>

          {/* 右侧步骤 */}
          <div className={styles.stepsWrapper}>
            {steps.map((step) => (
              <StepItem key={step.number} {...step} />
            ))}

            {/* CTA 按钮 */}
            <div className={styles.ctaWrapper}>
              <StartButton text={ctaText} href={targetUrl} className={styles.ctaButton} />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowToSection;
